import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Globe, Wifi, User, Phone, Home, Mail, ShieldAlert, Save, Loader2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { LeakMap } from "@/components/LeakMap";

const riskColor = (level) =>
  ({ crítico: "#ff2d6f", alto: "#ff9f43", medio: "#ffd166", bajo: "#22e07a" }[level] || "#00f0ff");
const sevColor = (s) => ({ medium: "#39d0ff", high: "#b026ff", critical: "#ff2d6f" }[s] || "#39d0ff");

export const Footprint = ({ user, onProfile }) => {
  const [fp, setFp] = useState(null);
  const [leaks, setLeaks] = useState(null);
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    phone: user?.phone || "",
    address: user?.address || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/footprint").then((r) => setFp(r.data)).catch(() => {});
    api.get("/leaks").then((r) => setLeaks(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", form);
      onProfile?.(data);
      const r = await api.get("/footprint");
      setFp(r.data);
      toast.success("Datos actualizados.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const input =
    "w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 font-sans text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-cyber-cyan focus:ring-1 focus:ring-cyber-cyan";
  const net = fp?.network;
  const home = net?.located ? { lat: net.lat, lng: net.lng } : null;

  return (
    <div className="space-y-8" data-testid="footprint">
      {/* Disclaimer */}
      <div className="flex items-start gap-3 rounded-xl border border-cyber-cyan/25 bg-cyber-cyan/[0.05] p-4">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-cyber-cyan" />
        <p className="font-sans text-xs leading-relaxed text-white/70">
          Esto muestra tu <b>huella digital real</b> (tu IP y su geolocalización) y los datos que <b>tú decides compartir</b>. La evaluación de filtraciones es una <b>simulación con fines de concienciación</b>: no consultamos bases de datos de brechas reales ni obtenemos datos personales de terceros.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Network / IP */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="footprint-ip">
          <h3 className="flex items-center gap-2 font-head text-sm font-semibold text-white">
            <Wifi className="h-4 w-4 text-cyber-cyan" /> Tu conexión
          </h3>
          {!fp ? (
            <div className="mt-6 flex items-center gap-2 text-white/40"><Loader2 className="h-4 w-4 animate-spin" /> Analizando...</div>
          ) : (
            <div className="mt-5 space-y-3 font-sans text-sm">
              <Row icon={Globe} label="IP pública" value={net.ip} mono />
              {net.located ? (
                <>
                  <Row icon={Home} label="Ubicación" value={`${net.city || "—"}, ${net.region || ""} · ${net.country || ""}`} />
                  <Row icon={Wifi} label="Proveedor (ISP)" value={net.isp || "—"} />
                  <Row icon={Globe} label="Zona horaria" value={net.timezone || "—"} />
                </>
              ) : (
                <p className="text-white/40">No se pudo geolocalizar esta IP.</p>
              )}
            </div>
          )}
        </div>

        {/* Shared data form */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
          <h3 className="flex items-center gap-2 font-head text-sm font-semibold text-white">
            <User className="h-4 w-4 text-cyber-cyan" /> Tus datos expuestos
          </h3>
          <p className="mt-1 font-sans text-xs text-white/45">Datos que has compartido. Cuantos más facilitas, mayor tu huella.</p>
          <div className="mt-4 mb-4 flex items-center gap-2 rounded-lg bg-white/[0.03] px-3 py-2.5">
            <Mail className="h-4 w-4 text-cyber-cyan" />
            <span className="font-mono-tech text-xs text-white/80">{user?.email}</span>
          </div>
          <form onSubmit={save} className="space-y-3" data-testid="footprint-form">
            <input className={input} placeholder="Nombre y apellidos" value={form.full_name} onChange={set("full_name")} data-testid="fp-name" />
            <input className={input} placeholder="Teléfono" value={form.phone} onChange={set("phone")} data-testid="fp-phone" />
            <input className={input} placeholder="Domicilio" value={form.address} onChange={set("address")} data-testid="fp-address" />
            <button type="submit" disabled={saving} data-testid="fp-save"
              className="inline-flex items-center gap-2 rounded-full bg-cyber-cyan px-6 py-2.5 font-mono-tech text-[11px] font-bold uppercase tracking-widest text-[#04060a] transition-transform duration-300 hover:scale-105 disabled:opacity-60">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Guardar
            </button>
          </form>
        </div>
      </div>

      {/* Leak assessment */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="leak-summary">
          <div>
            <h3 className="flex items-center gap-2 font-head text-lg font-bold text-white">
              <ShieldAlert className="h-5 w-5 text-cyber-cyan" /> Posibles filtraciones
            </h3>
            <p className="mt-1 font-sans text-sm text-white/55">
              {leaks ? `${leaks.total} brechas donde tu email podría estar expuesto` : "Evaluando..."}
            </p>
          </div>
          {leaks && (
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-white/50">Riesgo</p>
                <p className="font-head text-2xl font-black uppercase" style={{ color: riskColor(leaks.level) }}>{leaks.level}</p>
              </div>
              <div className="flex h-16 w-16 items-center justify-center rounded-full font-head text-2xl font-black"
                style={{ color: riskColor(leaks.level), border: `2px solid ${riskColor(leaks.level)}`, boxShadow: `0 0 24px ${riskColor(leaks.level)}55` }}>
                {leaks.risk}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1fr]">
          <div className="space-y-3">
            {leaks?.leaks.map((l) => (
              <div key={l.id} className="flex items-start justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="leak-item">
                <div>
                  <p className="font-head text-sm font-semibold text-white">{l.source} <span className="font-mono-tech text-xs text-white/40">· {l.year}</span></p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {l.exposed.map((x) => (
                      <span key={x} className="rounded-full bg-white/[0.05] px-2 py-0.5 font-mono-tech text-[10px] text-white/60">{x}</span>
                    ))}
                  </div>
                </div>
                <span className="shrink-0 rounded-full px-2 py-0.5 font-mono-tech text-[10px] uppercase"
                  style={{ color: sevColor(l.severity), background: `${sevColor(l.severity)}22` }}>{l.severity}</span>
              </div>
            ))}
          </div>
          <div>
            <LeakMap leaks={leaks?.leaks || []} home={home} />
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ icon: Icon, label, value, mono }) => (
  <div className="flex items-center justify-between border-b border-white/5 pb-2">
    <span className="flex items-center gap-2 text-white/50"><Icon className="h-3.5 w-3.5 text-cyber-cyan" /> {label}</span>
    <span className={`text-right text-white ${mono ? "font-mono-tech text-xs" : ""}`}>{value}</span>
  </div>
);
